import React, { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { Popover, Transition, Dialog } from "@headlessui/react";
import {
    Bars3Icon,
    PlusIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
    MagnifyingGlassIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/solid";
import { useSystemConfig } from "../context/systemConfigContext";

export default function Header(props) {
    function classNames(...classes) {
        return classes.filter(Boolean).join(" ");
    }

    const [value, setValue] = useState(props.value);
    const { systemStatus } = useSystemConfig();

    const handleKeyDown = (e) => {
        if (e.code === "Enter") {
            props.setSearch(e.target.value);
        }
    };

    const getUsageLevel = () => {
        if (!systemStatus || !systemStatus.maxMonthlyConsultations) return null;

        const percentage =
            (systemStatus.currentMonthConsultations /
                systemStatus.maxMonthlyConsultations) *
            100;

        if (percentage >= 96) {
            return {
                level: "critical",
                color: "red",
                bgClass: "bg-red-100 dark:bg-red-900",
                textClass: "text-red-800 dark:text-red-200",
                iconClass: "text-red-600 dark:text-red-300",
                icon: XCircleIcon,
                message: `Límite alcanzado (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
            };
        } else if (percentage >= 81) {
            return {
                level: "warning-high",
                color: "orange",
                bgClass: "bg-orange-100 dark:bg-orange-900",
                textClass: "text-orange-800 dark:text-orange-200",
                iconClass: "text-orange-600 dark:text-orange-300",
                icon: ExclamationTriangleIcon,
                message: `Muy cerca del límite (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
            };
        } else if (percentage >= 61) {
            return {
                level: "warning",
                color: "yellow",
                bgClass: "bg-yellow-100 dark:bg-yellow-900",
                textClass: "text-yellow-800 dark:text-yellow-200",
                iconClass: "text-yellow-600 dark:text-yellow-300",
                icon: ExclamationTriangleIcon,
                message: `Acercándose al límite (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
            };
        } else if (percentage > 0) {
            return {
                level: "normal",
                color: "green",
                bgClass: "bg-green-100 dark:bg-green-900",
                textClass: "text-green-800 dark:text-green-200",
                iconClass: "text-green-600 dark:text-green-300",
                icon: CheckCircleIcon,
                message: `Consultas: ${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations}`,
            };
        }

        return null;
    };

    const renderSystemStatusIndicator = () => {
        if (!systemStatus) return null;

        if (!systemStatus.systemEnabled) {
            return (
                <div className="flex items-center px-3 py-1 mr-2 bg-red-100 dark:bg-red-900 rounded-md">
                    <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-300 mr-2" />
                    <span className="text-sm font-medium text-red-800 dark:text-red-200">
                        Sistema Deshabilitado
                    </span>
                </div>
            );
        }

        const usageLevel = getUsageLevel();

        if (!usageLevel) return null;

        const Icon = usageLevel.icon;

        return (
            <div
                className={`flex items-center px-3 py-1 mr-2 ${usageLevel.bgClass} rounded-md`}
            >
                <Icon className={`h-5 w-5 ${usageLevel.iconClass} mr-2`} />
                <span className={`text-sm font-medium ${usageLevel.textClass}`}>
                    {usageLevel.message}
                </span>
            </div>
        );
    };

    return (
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white dark:bg-gray-900 shadow">
            <button
                type="button"
                className="px-2 xs:px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary md:hidden"
                onClick={() => props.setSidebarOpen(true)}
            >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex-1 flex w-full items-center px-2 justify-start">
                {props.search ? (
                    <div className="w-full ml-2 sm:ml-8 lg:ml-16">
                        <label htmlFor="search" className="sr-only">
                            Search
                        </label>
                        <div className="relative">
                            <div className="absolute w-full inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon
                                    className="h-5 w-5 text-blue-600 dark:text-primary"
                                    aria-hidden="true"
                                />
                            </div>
                            <input
                                id="search"
                                name="search"
                                onKeyDown={(event) => handleKeyDown(event)}
                                onChange={(event) =>
                                    setValue(event.target.value)
                                }
                                value={value}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm input-base"
                                placeholder={props.searchPlaceholder}
                                type="search"
                            />
                        </div>
                    </div>
                ) : null}
            </div>
            <div className="flex-1 flex items-center px-2 justify-end">
                {renderSystemStatusIndicator()}
                {props.buttonText ? (
                    <Link
                        to={props.href}
                        className="inline-flex mr-2 sm:mr-8 lg:mr-16 items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
                    >
                        <PlusIcon
                            className="-ml-1 mr-2 h-5 w-5"
                            aria-hidden="true"
                        />
                        {props.buttonText}
                    </Link>
                ) : null}
            </div>
        </div>
    );
}
